import React from 'react';
import * as cls from './build-with-voltaware.module.css';
import { Section } from '../../../../ui/components/section';
import { Typography } from '../../../../ui/components/typography';
import { IMG_PATH, ROUTES_PATHS } from '../../../../constants';
import { Link } from 'react-router-dom';
import { Button } from '../../../../ui/components/button';

const items = [
  {
    image: 'voltaware-dashboards.png',
    imageAlt:
      'dashboard showing household consumption breakdown, usage graphs, and CO₂ insights',
    icon: 'ico-dashboards.svg',
    title: 'Dashboards',
    texts: [
      <Typography className={cls.description}>
        <strong>Dashboards for Digital, CX and Insight teams.</strong>
      </Typography>,
      <Typography className={cls.description}>
        Use Voltaware dashboards to analyse appliance usage, trends, engagement,
        ROI and CO₂ savings, and embed key charts and KPIs into your own
        portals.
      </Typography>,
    ],
  },
  {
    image: 'api.png',
    imageAlt:
      'data flow, integration diagram - Voltaware data feeding multiple connected applications',
    icon: 'ico-api-as-a-service.svg',
    title: 'APIs as a Service',
    texts: [
      <Typography className={cls.description}>
        <strong>
          Integrate insights into apps, portals, billing and service journeys.
        </strong>
      </Typography>,
      <Typography className={cls.description}>
        Access real-time and historical outputs through REST APIs (and optional
        event delivery where needed). Get structured data, from appliance events
        and usage breakdowns to behavioural indicators, designed to plug into
        product features and operational workflows.
      </Typography>,
      <ul className={cls.list}>
        <li>
          <Typography>
            Works with smart-meter feeds and Voltaware sensors (single analytics
            layer).
          </Typography>
        </li>
        <li>
          <Typography>
            Designed for common use cases: high-bill support, personalised tips,
            EV/heat-pump journeys, segmentation and targeting.
          </Typography>
        </li>
      </ul>,
      <Link to={ROUTES_PATHS.INTEGRATE_WITH_VOLTAWARE}>
        <Button className={cls.buttonMobile} variant="green-transparent">
          Learn more
        </Button>
        <Button variant="green-transparent">
          Learn more about integration options
        </Button>
      </Link>,
      <a
        href="https://developers.voltaware.com/"
        target="_blank"
        rel="noopener noreferrer"
      >
        <Button variant="green-transparent">API documentation</Button>
      </a>,
    ],
  },
  {
    image: 'white-label-apps.png',
    imageAlt:
      'branded app mockup on phone and laptop screens with energy charts and alerts',
    icon: 'ico-white-label-apps.svg',
    title: 'White-Label Apps',
    texts: [
      <Typography className={cls.description}>
        <strong>Launch fast under your own brand.</strong>
      </Typography>,
      <Typography className={cls.description}>
        Voltaware’s white-label web and mobile apps help you deliver appliance
        breakdowns, usage trends, and alerts in a branded utility app and
        portal, powered by Voltaware’s backend and analytics.
      </Typography>,
    ],
  },
  {
    image: 'security.png',
    imageAlt: 'shield overlay secure data cloud visual',
    icon: 'ico-security.svg',
    title: 'Security and Compliance',
    texts: [
      <Typography className={cls.description}>
        <strong>Built for regulated environments.</strong>
      </Typography>,
      <Typography className={cls.description}>
        Voltaware applies <strong>GDPR</strong> and{' '}
        <strong>privacy-by-design</strong> principles across all integrations.
      </Typography>,
      <Typography className={cls.description}>
        The Voltaware sensor is certified to UK safety, health, and
        environmental standards, and is <b>RoHS</b> and <b>REACH compliant</b>,{' '}
        <b>EMC-tested</b>, and designed for reliable wireless connectivity with
        strong signal stability.
      </Typography>,
      <Typography className={cls.description}>
        All communications use <strong>end-to-end encryption</strong>, and data
        remains <strong>anonymised and securely stored</strong> throughout
        processing.
      </Typography>,
    ],
  },
];

export const BuildWithVoltaware = () => {
  return (
    <>
      {items.map((item, i) => (
        <Section
          backgroundContrast={i % 2 === 0}
          classNameRoot={cls.section}
          key={i}
        >
          {i === 0 && (
            <Typography as="h1" tag="h2">
              Build With Voltaware
            </Typography>
          )}

          <div className={cls.wrapper}>
            <div className={cls.textWrapper}>
              <div className={cls.titleWrapper}>
                <img
                  src={`${IMG_PATH}/our-technology/apis-and-integrations/${item.icon}`}
                  alt=""
                />
                <Typography className={cls.title}>{item.title}</Typography>
              </div>

              {item.texts.map((text, j) => (
                <React.Fragment key={j}>{text}</React.Fragment>
              ))}
            </div>

            <div className={cls.imgWrapper}>
              <img
                src={`${IMG_PATH}/our-technology/apis-and-integrations/${item.image}`}
                alt={item.imageAlt}
              />
            </div>
          </div>
        </Section>
      ))}
    </>
  );
};
